import React from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Save, X, Edit2, CreditCard, Building2, FileText, Plus, Trash2, Upload, Paperclip, Download } from 'lucide-react';
import { baseURL } from '@/lib/axios';
import { 
  bankingInfoSchema, 
  validateIFSCCode, 
  validateAccountNumber, 
  validateBankCode, 
  validateBranchCode, 
  validateSwiftCode, 
  validateIBANCode,
  validateAccountHolderName,
  validateBankName,
  validateBankBranch
} from '@/utils/validation';

// Floating Label Input Component
const FloatingLabelInput = ({
  label,
  value,
  onChange,
  type = "text",
  required = false,
  disabled = false,
  className = "",
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}) => {
  const [isFocused, setIsFocused] = React.useState(false);
  const hasValue = value && value.length > 0;

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <Input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder=""
          disabled={disabled}
          className={`h-10 text-sm pt-4 pb-1 px-3 bg-white border-gray-200 focus:border-blue-300 ${
            hasValue || isFocused ? "border-blue-300" : ""
          }`}
        />
        <Label
          className={`absolute left-3 transition-all duration-200 pointer-events-none ${
            hasValue
              ? "text-xs text-gray-500 -top-2 bg-white px-1"
              : isFocused
              ? "text-xs text-blue-600 -translate-y-2 top-1"
              : "text-sm text-gray-500 top-3"
          }`}
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Label>
      </div>
    </div>
  );
};

// Floating Label Select Component
const FloatingLabelSelect = ({
  label,
  value,
  onChange,
  options = [],
  required = false,
  disabled = false,
  className = "",
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  required?: boolean;
  disabled?: boolean;
  className?: string;
}) => {
  const [isFocused, setIsFocused] = React.useState(false);
  const hasValue = value && value.length > 0;

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <Select
          value={value || ""}
          onValueChange={onChange}
          onOpenChange={(open) => setIsFocused(open)}
          disabled={disabled}
        >
          <SelectTrigger
            className={`bg-white border-gray-200 focus:border-blue-300 h-10 text-sm pt-4 pb-1 px-3 ${
              hasValue || isFocused ? "border-blue-300" : ""
            }`}
          >
            <SelectValue placeholder="" />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Label
          className={`absolute left-3 transition-all duration-200 pointer-events-none ${
            hasValue
              ? "text-xs text-gray-500 -top-2 bg-white px-1"
              : isFocused
              ? "text-xs text-blue-600 -translate-y-2 top-1"
              : "text-sm text-gray-500 top-3"
          }`}
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Label>
      </div>
    </div>
  );
};

// Type definitions for banking information
interface BankingInfo {
  countryName: string;
  accountHolderName: string;
  bankName: string;
  bankBranch: string;
  accountNumber: string;
  accountType: string;
  attachments: string[];
  ifscCode: string;
  ibanCode: string;
  bankCode: string;
  branchCode: string;
  swiftCode: string;
  status: string;
  hasNewFiles?: boolean; // Optional flag to indicate new files
}

interface BankingSectionProps {
  bankingData: BankingInfo[];
  setBankingData: (data: BankingInfo[], files?: File[]) => void;
  isEditing: boolean;
}

// Bank Information Section
export const BankInfoSection = ({ bankingData, setBankingData }: BankingSectionProps) => {
  const [localIsEditing, setLocalIsEditing] = React.useState(false);
  const [localData, setLocalData] = React.useState(bankingData);
  const [uploadedFiles, setUploadedFiles] = React.useState<{ [key: number]: File[] }>({});
  const [isDragOver, setIsDragOver] = React.useState<{ [key: number]: boolean }>({});
  const [selectedDocument, setSelectedDocument] = React.useState<{ url: string; title: string; type: string; fileName?: string } | null>(null);
  const [fieldValidation, setFieldValidation] = React.useState<{ [key: string]: { isValid: boolean; message: string } }>({});
  const [showRemoveConfirmation, setShowRemoveConfirmation] = React.useState(false);
  const [accountToRemove, setAccountToRemove] = React.useState<number | null>(null);

  React.useEffect(() => {
    setLocalData(bankingData);
  }, [bankingData]);

  // Real-time validation function
  const validateField = (field: string, value: string, index: number) => {
    const fieldKey = `${index}-${field}`;
    let isValid = true;
    let message = '';

    switch (field) {
      case 'countryName':
        if (!value.trim()) {
          isValid = false;
          message = 'Country name is required';
        } else if (value.trim().length < 2) {
          isValid = false;
          message = 'Country name must be at least 2 characters';
        } else if (!/^[a-zA-Z\s]+$/.test(value)) {
          isValid = false;
          message = 'Country name can only contain letters and spaces';
        }
        break;
      
      case 'accountHolderName':
        if (!value.trim()) {
          isValid = false;
          message = 'Account holder name is required';
        } else if (!validateAccountHolderName(value)) {
          isValid = false;
          message = 'Account holder name can only contain letters, spaces, and common punctuation';
        }
        break;
      
      case 'bankName':
        if (!value.trim()) {
          isValid = false;
          message = 'Bank name is required';
        } else if (!validateBankName(value)) {
          isValid = false;
          message = 'Bank name can only contain letters, spaces, and common punctuation';
        }
        break;
      
      case 'bankBranch':
        if (!value.trim()) {
          isValid = false;
          message = 'Bank branch is required';
        } else if (!validateBankBranch(value)) {
          isValid = false;
          message = 'Bank branch can only contain letters, numbers, spaces, and common punctuation';
        }
        break;
      
      case 'accountNumber':
        if (!value.trim()) {
          isValid = false;
          message = 'Account number is required';
        } else if (!validateAccountNumber(value)) {
          isValid = false;
          message = 'Account number must be 9-18 digits';
        }
        break;
      
      case 'ifscCode':
        if (!value.trim()) {
          isValid = false;
          message = 'IFSC code is required';
        } else if (!validateIFSCCode(value)) {
          isValid = false;
          message = 'IFSC code must be in format: 4 letters followed by 7 alphanumeric characters';
        }
        break;
      
      case 'bankCode':
        if (!value.trim()) {
          isValid = false;
          message = 'Bank code is required';
        } else if (!validateBankCode(value)) {
          isValid = false;
          message = 'Bank code must be 3-6 alphanumeric characters';
        }
        break;
      
      case 'branchCode':
        if (value.trim() && !validateBranchCode(value)) {
          isValid = false;
          message = 'Branch code must be 3-6 alphanumeric characters';
        }
        break;
      
      case 'swiftCode':
        if (value.trim() && !validateSwiftCode(value)) {
          isValid = false;
          message = 'SWIFT code must be 8 or 11 letters';
        }
        break;
      
      case 'ibanCode':
        if (value.trim() && !validateIBANCode(value)) {
          isValid = false;
          message = 'IBAN code must be in format: 2 letters + 2 digits + 4-30 alphanumeric characters';
        }
        break;
    }

    setFieldValidation(prev => ({
      ...prev,
      [fieldKey]: { isValid, message }
    }));

    return isValid;
  };

  // Input formatting functions
  const formatIFSCCode = (value: string) => {
    return value.replace(/\s/g, '').toUpperCase();
  };

  const formatAccountNumber = (value: string) => {
    return value.replace(/\D/g, '');
  };

  const formatBankCode = (value: string) => {
    return value.replace(/\s/g, '').toUpperCase();
  };

  const formatSwiftCode = (value: string) => {
    return value.replace(/\s/g, '').toUpperCase();
  };

  const formatIBANCode = (value: string) => {
    return value.replace(/\s/g, '').toUpperCase();
  };

  // Helper function to get validation status for a field
  const getFieldValidationStatus = (field: string, index: number) => {
    const fieldKey = `${index}-${field}`;
    return fieldValidation[fieldKey] || { isValid: true, message: '' };
  };


  // Helper function to extract hash from document URL (same as onboarding)
  const getDocumentHash = (docUrl: string) => {
    if (!docUrl) return 'document.pdf';
    const parts = docUrl.split('/');
    return parts[parts.length - 1] || 'document.pdf';
  };

  // Function to get complete backend URL for documents (same as onboarding)
  const getCompleteBackendUrl = (docUrl: string) => {
    if (!docUrl) return '';
    const baseUrl = baseURL.replace('/api/v1', '');
    const hash = getDocumentHash(docUrl);
    return `${baseUrl}/uploads/bank-attachments/${hash}`;
  };

  const handleSave = () => {
    
    // Validate each banking account entry
    const validationErrors: string[] = [];
    let hasValidationErrors = false;
    
    localData.forEach((account, index) => {
      
      // Check if account has any data at all
      const hasAnyData = Object.values(account).some(value => 
        value && value.toString().trim() !== '' && value !== 'Active' && value !== 'Current'
      );
      
      // If account has no data, skip validation
      if (!hasAnyData) {
        return;
      }
      
      // Check individual field validations and collect specific errors
      const requiredFields = ['countryName', 'accountHolderName', 'bankName', 'bankBranch', 'accountNumber', 'accountType', 'ifscCode', 'bankCode'];
      const fieldErrors: string[] = [];
      
      // Check required fields - only validate if field has value
      for (const field of requiredFields) {
        const fieldValue = account[field as keyof BankingInfo];
        // Only validate if field has a value (not empty)
        if (fieldValue && fieldValue.toString().trim() !== '') {
          const validation = getFieldValidationStatus(field, index);
          if (!validation.isValid) {
            fieldErrors.push(`${field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}: ${validation.message}`);
          }
        } else {
          // If required field is empty, show error
          fieldErrors.push(`${field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}: This field is required`);
        }
      }
      
      // Check attachments
      const hasAttachments = account.attachments && account.attachments.length > 0;
      const hasUploadedFiles = uploadedFiles[index] && uploadedFiles[index].length > 0;
      if (!hasAttachments && !hasUploadedFiles) {
        fieldErrors.push('At least one attachment is required');
      }
      
      // If there are field errors, add them to validation errors
      if (fieldErrors.length > 0) {
        hasValidationErrors = true;
        validationErrors.push(`Account ${index + 1}: ${fieldErrors.join(', ')}`);
      }
      
      // Also validate using schema for additional checks
      try {
        bankingInfoSchema.parse(account);
      } catch (error: any) {
        hasValidationErrors = true;
        if (error.errors) {
          error.errors.forEach((err: any) => {
            validationErrors.push(`Account ${index + 1}: ${err.message}`);
          });
        }
      }
    });

    if (hasValidationErrors) {
      
      // Show immediate toast notifications for all validation errors
      validationErrors.forEach((error, _index) => {
        toast.error(error);
      });
      
      // Also show a summary toast
      toast.error(`❌ Banking Information Validation Failed: ${validationErrors.length} error(s) found. Please fix all errors before saving.`);
      return;
    }

    // Collect files for upload
    const filesToUpload: File[] = [];
    Object.values(uploadedFiles).forEach(files => {
      if (files && files.length > 0) {
        filesToUpload.push(...files);
      }
    });

    // Debug: Log the data being sent

    // Pass the banking data directly - the parent component will wrap it properly
    // Don't clean up hasNewFiles here - let the backend handle it
    setBankingData(localData, filesToUpload);
    setLocalIsEditing(false);
    setUploadedFiles({});
    setFieldValidation({});
    toast.success('Banking information updated successfully');
  };

  const handleCancel = () => {
    setLocalData(bankingData);
    setLocalIsEditing(false);
    setUploadedFiles({});
    setIsDragOver({});
    // No toast notification for cancel
  };

  const handleEdit = () => {
    setLocalIsEditing(true);
    // No toast notification for edit mode
  };

  const handleFileUpload = (index: number, files: FileList | File[]) => {
    const fileArray = Array.from(files);
    
    // Validate each file
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    const maxFilesPerAccount = 20; // Increased from 5 to 20 files per account
    
    // Check total file count for this account
    const currentFiles = uploadedFiles[index] || [];
    const existingAttachments = localData[index]?.attachments || [];
    const totalFiles = currentFiles.length + existingAttachments.length + fileArray.length;
    
    if (totalFiles > maxFilesPerAccount) {
      toast.error(`❌ File Upload Failed: Maximum ${maxFilesPerAccount} files allowed per account`);
      return;
    }
    
    for (const file of fileArray) {
      if (file.size > maxSize) {
        toast.error(`❌ File Upload Failed: ${file.name} size should be less than 10MB`);
        return;
      }
      
      if (!allowedTypes.includes(file.type)) {
        toast.error(`❌ File Upload Failed: ${file.name} must be PDF, JPG, JPEG, or PNG`);
        return;
      }
      
      // Check for duplicate file names
      const isDuplicate = currentFiles.some(existingFile => existingFile.name === file.name) ||
                         existingAttachments.some(attachment => attachment === file.name);
      
      if (isDuplicate) {
        toast.error(`❌ File Upload Failed: ${file.name} already exists`);
        return;
      }
    }

    // Update uploaded files state for temporary display
    setUploadedFiles(prev => ({ 
      ...prev, 
      [index]: [...(prev[index] || []), ...fileArray]
    }));
    
    // Simple approach: Add placeholder to attachments array to indicate new files
    const newBankingData = [...localData];
    if (newBankingData[index]) {
      // Add placeholder entries for new files
      const placeholders = fileArray.map(() => 'NEW_FILE_PLACEHOLDER');
      newBankingData[index].attachments = [...(newBankingData[index].attachments || []), ...placeholders];
      setLocalData(newBankingData);
    }
    
    // File upload success - no toast notification
  };

  const handleFileChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileUpload(index, files);
    }
  };

  const handleDragOver = (index: number, e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(prev => ({ ...prev, [index]: true }));
  };

  const handleDragLeave = (index: number, e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(prev => ({ ...prev, [index]: false }));
  };

  const handleDrop = (index: number, e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(prev => ({ ...prev, [index]: false }));
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileUpload(index, files);
    }
  };

  const removeFile = (index: number, fileIndex?: number) => {
    if (fileIndex !== undefined) {
      // Check if we're removing from uploaded files or existing attachments
      const hasUploadedFiles = uploadedFiles[index] && uploadedFiles[index].length > 0;
      const hasExistingAttachments = localData[index]?.attachments && localData[index].attachments.length > 0;
      
      if (hasUploadedFiles && fileIndex < uploadedFiles[index].length) {
        // Remove from uploaded files (not yet saved)
        setUploadedFiles(prev => {
          const newFiles = { ...prev };
          if (newFiles[index]) {
            newFiles[index] = newFiles[index].filter((_, i) => i !== fileIndex);
            if (newFiles[index].length === 0) {
              delete newFiles[index];
            }
          }
          return newFiles;
        });
      } else if (hasExistingAttachments) {
        // Remove from existing attachments (already saved)
        const newBankingData = [...localData];
        if (newBankingData[index] && newBankingData[index].attachments) {
          // Filter out placeholders first to get actual attachments
          const actualAttachments = newBankingData[index].attachments.filter(att => att !== 'NEW_FILE_PLACEHOLDER');
          const adjustedIndex = hasUploadedFiles ? fileIndex - uploadedFiles[index].length : fileIndex;
          
          if (adjustedIndex >= 0 && adjustedIndex < actualAttachments.length) {
            // Find the original index in the full array
            let originalIndex = 0;
            let actualIndex = 0;
            for (let i = 0; i < newBankingData[index].attachments.length; i++) {
              if (newBankingData[index].attachments[i] !== 'NEW_FILE_PLACEHOLDER') {
                if (actualIndex === adjustedIndex) {
                  originalIndex = i;
                  break;
                }
                actualIndex++;
              }
            }
            
            newBankingData[index].attachments = newBankingData[index].attachments.filter((_, i) => i !== originalIndex);
            setLocalData(newBankingData);
          }
        }
      }
      
      toast.info('File removed');
    } else {
      // Remove all files for this banking entry
      setUploadedFiles(prev => {
        const newFiles = { ...prev };
        delete newFiles[index];
        return newFiles;
      });
      
      // Also clear the attachments field in the local data
      const newBankingData = [...localData];
      if (newBankingData[index]) {
        newBankingData[index].attachments = [];
        setLocalData(newBankingData);
      }
      
      toast.info('All files removed');
    }
  };


  // Function to handle document viewing (same as onboarding)
  const handleViewDocument = (docUrl: string, title: string, type: string, fileName?: string) => {
    if (!docUrl) {
      toast.error('No document available for viewing');
      return;
    }
    setSelectedDocument({ url: docUrl, title, type, fileName });
  };

  // Function to handle document download
  const handleDownloadDocument = async (docUrl: string, fileName?: string) => {
    if (!docUrl) {
      toast.error('No document available for download');
      return;
    }
    
    try {
      const url = getCompleteBackendUrl(docUrl);
      
      // Get authorization token
      const token = localStorage.getItem('accessToken');
      
      // Fetch the file as a blob with authorization
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/pdf,application/octet-stream,*/*',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      
      // Create a blob URL and trigger download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName || 'document.pdf';
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl);
      
      toast.success('Document downloaded successfully');
    } catch (error) {
      toast.error('Failed to download document. Please try again.');
    }
  };

  const addBankAccount = () => {
    setLocalData([
      ...localData,
      {
        countryName: '',
        accountHolderName: '',
        bankName: '',
        bankBranch: '',
        accountNumber: '',
        accountType: '',
        attachments: [],
        ifscCode: '',
        ibanCode: '',
        bankCode: '',
        branchCode: '',
        swiftCode: '',
        status: 'Active'
      }
    ]);
  };

  const removeBankAccount = (index: number) => {
    setAccountToRemove(index);
    setShowRemoveConfirmation(true);
  };

  const confirmRemoveAccount = () => {
    if (accountToRemove === null) return;

    try {
      const newBankingData = localData.filter((_, i) => i !== accountToRemove);
      
      
      // Update local state immediately for better UX
      setLocalData(newBankingData);
      
      // Save to database immediately
      setBankingData(newBankingData, []);
      
      // Clear uploaded files for the removed account
      setUploadedFiles(prev => {
        const newFiles = { ...prev };
        delete newFiles[accountToRemove];
        
        // Shift file indices for accounts after the removed one
        const shiftedFiles: { [key: number]: File[] } = {};
        Object.keys(newFiles).forEach(key => {
          const index = parseInt(key);
          if (index > accountToRemove) {
            shiftedFiles[index - 1] = newFiles[index];
          } else if (index < accountToRemove) {
            shiftedFiles[index] = newFiles[index];
          }
        });
        
        return shiftedFiles;
      });
      
      // Clear field validation for the removed account
      setFieldValidation(prev => {
        const newValidation: { [key: string]: { isValid: boolean; message: string } } = {};
        Object.keys(prev).forEach(key => {
          const [accountIndex, field] = key.split('-');
          const index = parseInt(accountIndex);
          if (index !== accountToRemove) {
            if (index > accountToRemove) {
              newValidation[`${index - 1}-${field}`] = prev[key];
            } else {
              newValidation[key] = prev[key];
            }
          }
        });
        return newValidation;
      });
      
      toast.success('Bank account removed successfully');
    } catch (error) {
      console.error('Error removing bank account:', error);
      toast.error('Failed to remove bank account');
      
      // Revert local state on error
      setLocalData(bankingData);
    } finally {
      setShowRemoveConfirmation(false);
      setAccountToRemove(null);
    }
  };

  const cancelRemoveAccount = () => {
    setShowRemoveConfirmation(false);
    setAccountToRemove(null);
  };

  const updateBankAccount = (index: number, field: keyof BankingInfo, value: string) => {
    let formattedValue = value;
    
    // Apply formatting based on field type
    switch (field) {
      case 'ifscCode':
        formattedValue = formatIFSCCode(value);
        break;
      case 'accountNumber':
        formattedValue = formatAccountNumber(value);
        break;
      case 'bankCode':
        formattedValue = formatBankCode(value);
        break;
      case 'branchCode':
        formattedValue = formatBankCode(value);
        break;
      case 'swiftCode':
        formattedValue = formatSwiftCode(value);
        break;
      case 'ibanCode':
        formattedValue = formatIBANCode(value);
        break;
      case 'accountHolderName':
      case 'bankName':
      case 'bankBranch':
      case 'countryName':
        // Trim whitespace for text fields
        formattedValue = value.trim();
        break;
    }

    const newBankingData = [...localData];
    newBankingData[index] = {
      ...newBankingData[index],
      [field]: formattedValue
    };
    setLocalData(newBankingData);

    // Validate the field in real-time (but don't show toast notifications)
    validateField(field, formattedValue, index);
    
    // No real-time toast notifications - only show on save button click
  };

  return (
    <>
    <Card className="border border-gray-200 shadow-sm bg-white rounded-lg">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
              <CreditCard className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-base font-semibold text-gray-900">
                Banking Information
              </CardTitle>
              </div>
          </div>
          <div className="flex items-center gap-1">
            {localIsEditing && (
              <Button
                variant="default"
                size="sm"
                onClick={handleSave}
                className="gap-1 bg-blue-600 hover:bg-blue-700 text-xs px-3 py-1"
              >
                <Save className="h-3 w-3" />
                Save
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={localIsEditing ? handleCancel : handleEdit}
              className="gap-1 hover:bg-gray-50 text-xs px-3 py-1"
            >
              {localIsEditing ? (
                <>
                  <X className="h-3 w-3" />
                  Cancel
                </>
              ) : (
                <>
                  <Edit2 className="h-3 w-3" />
                  Edit
                </>
              )}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        <div className="space-y-3">
          {localData && localData.map((account, index) => (
            <div key={index} className="bg-gray-50 rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
              {localIsEditing ? (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-blue-50 rounded-lg flex items-center justify-center">
                        <CreditCard className="h-3 w-3 text-blue-600" />
                      </div>
                      <h5 className="font-medium text-sm text-gray-700">Bank Account {index + 1}</h5>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removeBankAccount(index)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 text-xs px-2 py-1"
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Remove
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="bg-white rounded-lg border border-gray-200 p-2 hover:shadow-md transition-shadow">
                      <FloatingLabelInput
                        label="Country Name"
                        value={account.countryName}
                        onChange={(value) => updateBankAccount(index, 'countryName', value)}
                        required
                      />
                      {!getFieldValidationStatus('countryName', index).isValid && (
                        <p className="text-xs text-red-500 mt-1">
                          {getFieldValidationStatus('countryName', index).message}
                        </p>
                      )}
                    </div>
                    
                    <div className="bg-white rounded-lg border border-gray-200 p-2 hover:shadow-md transition-shadow">
                      <FloatingLabelInput
                        label="Account Holder Name"
                        value={account.accountHolderName}
                        onChange={(value) => updateBankAccount(index, 'accountHolderName', value)}
                        required
                      />
                      {!getFieldValidationStatus('accountHolderName', index).isValid && (
                        <p className="text-xs text-red-500 mt-1">
                          {getFieldValidationStatus('accountHolderName', index).message}
                        </p>
                      )}
                    </div>
                    
                    <div className="bg-white rounded-lg border border-gray-200 p-2 hover:shadow-md transition-shadow">
                      <FloatingLabelInput
                        label="Bank Name"
                        value={account.bankName}
                        onChange={(value) => updateBankAccount(index, 'bankName', value)}
                        required
                      />
                      {!getFieldValidationStatus('bankName', index).isValid && (
                        <p className="text-xs text-red-500 mt-1">
                          {getFieldValidationStatus('bankName', index).message}
                        </p>
                      )}
                    </div>
                    
                    <div className="bg-white rounded-lg border border-gray-200 p-2 hover:shadow-md transition-shadow">
                      <FloatingLabelInput
                        label="Bank Branch"
                        value={account.bankBranch}
                        onChange={(value) => updateBankAccount(index, 'bankBranch', value)}
                        required
                      />
                      {!getFieldValidationStatus('bankBranch', index).isValid && (
                        <p className="text-xs text-red-500 mt-1">
                          {getFieldValidationStatus('bankBranch', index).message}
                        </p>
                      )}
                    </div>
                    
                    <div className="bg-white rounded-lg border border-gray-200 p-2 hover:shadow-md transition-shadow">
                      <FloatingLabelInput
                        label="Account Number"
                        value={account.accountNumber}
                        onChange={(value) => updateBankAccount(index, 'accountNumber', value)}
                        required
                      />
                      {!getFieldValidationStatus('accountNumber', index).isValid && (
                        <p className="text-xs text-red-500 mt-1">
                          {getFieldValidationStatus('accountNumber', index).message}
                        </p>
                      )}
                    </div>
                    
                    <div className="bg-white rounded-lg border border-gray-200 p-2 hover:shadow-md transition-shadow">
                      <FloatingLabelSelect
                        label="Account Type"
                        value={account.accountType}
                        onChange={(value) => updateBankAccount(index, 'accountType', value)}
                        options={["Savings", "Current", "Fixed Deposit", "Recurring Deposit", "Salary", "NRI"]}
                        required
                      />
                    </div>
                    
                    <div className="bg-white rounded-lg border border-gray-200 p-2 hover:shadow-md transition-shadow">
                      <FloatingLabelInput
                        label="IFSC Code"
                        value={account.ifscCode}
                        onChange={(value) => updateBankAccount(index, 'ifscCode', value)}
                        required
                      />
                      {!getFieldValidationStatus('ifscCode', index).isValid && (
                        <p className="text-xs text-red-500 mt-1">
                          {getFieldValidationStatus('ifscCode', index).message}
                        </p>
                      )}
                    </div>
                    
                    <div className="bg-white rounded-lg border border-gray-200 p-2 hover:shadow-md transition-shadow">
                      <FloatingLabelInput
                        label="IBAN Code"
                        value={account.ibanCode}
                        onChange={(value) => updateBankAccount(index, 'ibanCode', value)}
                      />
                      {!getFieldValidationStatus('ibanCode', index).isValid && (
                        <p className="text-xs text-red-500 mt-1">
                          {getFieldValidationStatus('ibanCode', index).message}
                        </p>
                      )}
                    </div>
                    
                    <div className="bg-white rounded-lg border border-gray-200 p-2 hover:shadow-md transition-shadow">
                      <FloatingLabelInput
                        label="Bank Code"
                        value={account.bankCode}
                        onChange={(value) => updateBankAccount(index, 'bankCode', value)}
                        required
                      />
                      {!getFieldValidationStatus('bankCode', index).isValid && (
                        <p className="text-xs text-red-500 mt-1">
                          {getFieldValidationStatus('bankCode', index).message}
                        </p>
                      )}
                    </div>
                    
                    <div className="bg-white rounded-lg border border-gray-200 p-2 hover:shadow-md transition-shadow">
                      <FloatingLabelInput
                        label="Branch Code"
                        value={account.branchCode}
                        onChange={(value) => updateBankAccount(index, 'branchCode', value)}
                      />
                      {!getFieldValidationStatus('branchCode', index).isValid && (
                        <p className="text-xs text-red-500 mt-1">
                          {getFieldValidationStatus('branchCode', index).message}
                        </p>
                      )}
                    </div>
                    
                    <div className="bg-white rounded-lg border border-gray-200 p-2 hover:shadow-md transition-shadow">
                      <FloatingLabelInput
                        label="Swift Code"
                        value={account.swiftCode}
                        onChange={(value) => updateBankAccount(index, 'swiftCode', value)}
                      />
                      {!getFieldValidationStatus('swiftCode', index).isValid && (
                        <p className="text-xs text-red-500 mt-1">
                          {getFieldValidationStatus('swiftCode', index).message}
                        </p>
                      )}
                    </div>
                    
                    <div className="bg-white rounded-lg border border-gray-200 p-2 hover:shadow-md transition-shadow">
                      <FloatingLabelSelect
                        label="Status"
                        value={account.status}
                        onChange={(value) => updateBankAccount(index, 'status', value)}
                        options={["Active", "Inactive", "Pending", "Suspended"]}
                      />
                    </div>
                    
                    <div className="bg-white rounded-lg border border-gray-200 p-2 hover:shadow-md transition-shadow md:col-span-2">
                      <Label className="text-xs font-medium text-gray-700">Attachments <span className="text-red-500">*</span></Label>
                      {(!account.attachments || account.attachments.filter(att => att !== 'NEW_FILE_PLACEHOLDER').length === 0) && (!uploadedFiles[index] || uploadedFiles[index].length === 0) && (
                        <p className="text-xs text-red-500 mt-1">
                          At least one attachment is required
                        </p>
                      )}
                      
                      {/* Display all files - existing attachments first, then uploaded files */}
                      {((account.attachments && account.attachments.filter(att => att !== 'NEW_FILE_PLACEHOLDER').length > 0) || (uploadedFiles[index] && uploadedFiles[index].length > 0)) && (
                        <div className="space-y-2">
                          {/* Display existing attachments first (filter out placeholders) */}
                          {account.attachments && account.attachments
                            .filter(attachment => attachment !== 'NEW_FILE_PLACEHOLDER')
                            .map((attachment, attIndex) => (
                            <div key={`existing-${attIndex}`} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border">
                              <div className="flex items-center gap-2">
                                <Paperclip className="h-4 w-4 text-gray-500" />
                                <span className="text-sm text-gray-700">
                                  {attachment}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => removeFile(index, (uploadedFiles[index]?.length || 0) + attIndex)}
                                  className="h-6 text-xs"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                          
                          {/* Display uploaded files after existing attachments */}
                          {uploadedFiles[index] && uploadedFiles[index].map((file, fileIndex) => (
                            <div key={`uploaded-${fileIndex}`} className="flex items-center justify-between p-2 bg-blue-50 rounded-lg border border-blue-200">
                              <div className="flex items-center gap-2">
                                <Paperclip className="h-4 w-4 text-blue-500" />
                                <span className="text-sm text-blue-700">
                                  {file.name}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => removeFile(index, fileIndex)}
                                  className="h-6 text-xs"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Upload area */}
                      <div 
                        className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer ${
                          isDragOver[index] ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                        }`}
                        onDragOver={(e) => handleDragOver(index, e)}
                        onDragLeave={(e) => handleDragLeave(index, e)}
                        onDrop={(e) => handleDrop(index, e)}
                        onClick={() => document.getElementById(`file-upload-${index}`)?.click()}
                      >
                        <Upload className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                        <div className="text-sm">
                          <span className="text-blue-600 font-medium">Click to upload</span>
                          <span className="text-gray-500"> or drag and drop</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          PDF, JPG, JPEG, PNG up to 10MB (multiple files allowed)
                        </p>
                        <input
                          id={`file-upload-${index}`}
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          multiple
                          onChange={(e) => handleFileChange(index, e)}
                          className="hidden"
                        />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                        <CreditCard className="h-4 w-4 text-blue-600" />
                      </div>
                      <div>
                        <div className="font-medium text-sm text-gray-900">{account.accountHolderName || 'Account Holder'}</div>
                        <div className="text-gray-600 text-xs">{account.bankName || 'Bank Name'}</div>
                      </div>
                    </div>
                    <Badge 
                      variant="secondary" 
                      className={`text-xs font-medium px-3 py-1 rounded-full border ${
                        account.status === 'Active' ? 'bg-green-50 text-green-600 border-green-200' :
                        account.status === 'Inactive' ? 'bg-red-50 text-red-600 border-red-200' :
                        account.status === 'Suspended' ? 'bg-orange-50 text-orange-600 border-orange-200' :
                        'bg-yellow-50 text-yellow-600 border-yellow-200'
                      }`}
                    >
                      {account.status || 'Active'}
                    </Badge>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-1 border-t border-gray-200">
                    {account.countryName && (
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 bg-blue-50 rounded flex items-center justify-center">
                          <Building2 className="h-2 w-2 text-blue-600" />
                        </div>
                        <span className="text-xs text-gray-600">Country: {account.countryName}</span>
                      </div>
                    )}
                    {account.accountType && (
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 bg-blue-50 rounded flex items-center justify-center">
                          <CreditCard className="h-2 w-2 text-blue-600" />
                        </div>
                        <span className="text-xs text-gray-600">Type: {account.accountType}</span>
                      </div>
                    )}
                    {account.accountNumber && (
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 bg-blue-50 rounded flex items-center justify-center">
                          <CreditCard className="h-2 w-2 text-blue-600" />
                        </div>
                        <span className="text-xs text-gray-600">Account: {account.accountNumber}</span>
                      </div>
                    )}
                    {account.bankBranch && (
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 bg-blue-50 rounded flex items-center justify-center">
                          <Building2 className="h-2 w-2 text-blue-600" />
                        </div>
                        <span className="text-xs text-gray-600">Branch: {account.bankBranch}</span>
                      </div>
                    )}
                    {account.ifscCode && (
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 bg-blue-50 rounded flex items-center justify-center">
                          <FileText className="h-2 w-2 text-blue-600" />
                        </div>
                        <span className="text-xs text-gray-600">IFSC: {account.ifscCode}</span>
                      </div>
                    )}
                    {account.ibanCode && (
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 bg-blue-50 rounded flex items-center justify-center">
                          <FileText className="h-2 w-2 text-blue-600" />
                        </div>
                        <span className="text-xs text-gray-600">IBAN: {account.ibanCode}</span>
                      </div>
                    )}
                    {account.bankCode && (
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 bg-blue-50 rounded flex items-center justify-center">
                          <Building2 className="h-2 w-2 text-blue-600" />
                        </div>
                        <span className="text-xs text-gray-600">Bank Code: {account.bankCode}</span>
                      </div>
                    )}
                    {account.branchCode && (
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 bg-blue-50 rounded flex items-center justify-center">
                          <Building2 className="h-2 w-2 text-blue-600" />
                        </div>
                        <span className="text-xs text-gray-600">Branch Code: {account.branchCode}</span>
                      </div>
                    )}
                    {account.swiftCode && (
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 bg-blue-50 rounded flex items-center justify-center">
                          <FileText className="h-2 w-2 text-blue-600" />
                        </div>
                        <span className="text-xs text-gray-600">Swift: {account.swiftCode}</span>
                      </div>
                    )}
                    {account.attachments && account.attachments.length > 0 && (
                      <div className="space-y-1">
                        <div className="flex items-center gap-1">
                          <div className="w-4 h-4 bg-blue-50 rounded flex items-center justify-center">
                            <Paperclip className="h-2 w-2 text-blue-600" />
                          </div>
                          <span className="text-xs text-gray-600 font-medium">Attachments ({account.attachments.filter(att => att !== 'NEW_FILE_PLACEHOLDER').length}):</span>
                        </div>
                        <div className="space-y-1 ml-4">
                          {account.attachments
                            .filter(attachment => attachment !== 'NEW_FILE_PLACEHOLDER')
                            .map((attachment, attIndex) => (
                            <div key={attIndex} className="flex items-center gap-2">
                              <span className="text-xs text-gray-600">{attachment}</span>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleViewDocument(
                                    attachment,
                                    `Banking Document - ${account.bankName}`,
                                    'banking',
                                    attachment
                                  )}
                                  className="h-4 w-4 p-0 text-gray-400 hover:text-green-600"
                                  title="View Document"
                                >
                                  <FileText className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDownloadDocument(attachment, attachment)}
                                  className="h-4 w-4 p-0 text-gray-400 hover:text-blue-600"
                                  title="Download Document"
                                >
                                  <Download className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
          
          {localIsEditing && (
            <Button
              variant="outline"
              size="sm"
              onClick={addBankAccount}
              className="gap-1 w-full text-xs"
            >
              <Plus className="h-3 w-3" />
              Add Bank Account
            </Button>
          )}
          
          {!localIsEditing && localData.length === 0 && (
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center mx-auto mb-3">
                <CreditCard className="h-6 w-6 text-gray-400" />
              </div>
              <h3 className="text-sm font-medium text-gray-800 mb-1">No Bank Accounts</h3>
              <p className="text-xs text-gray-500 mb-4">Add your bank account information to get started</p>
              <Button
                variant="outline"
                size="sm"
                onClick={handleEdit}
                className="gap-1 border-dashed border-2 border-gray-300 hover:border-blue-400 hover:bg-blue-50 text-xs"
              >
                <Plus className="h-3 w-3" />
                Add Bank Account
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>

    {/* Document Viewer Modal (same as onboarding) */}
    {selectedDocument && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-4xl max-h-[90vh] w-full mx-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">{selectedDocument.title}</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedDocument(null)}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium text-gray-700">Document Type: {selectedDocument.type}</span>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-3 border border-blue-200">
            <a 
              href={getCompleteBackendUrl(selectedDocument.url)} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline break-all text-sm font-medium cursor-pointer block bg-gray-50 p-2 rounded border"
            >
              {selectedDocument.fileName || 'document.pdf'}
            </a>
          </div>
          
          <div className="flex gap-2 mt-4">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                const url = getCompleteBackendUrl(selectedDocument.url);
                window.open(url, '_blank');
                toast.info("Document opened in new tab. Right-click and 'Save as' to download.");
              }}
              className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
            >
              <FileText className="h-4 w-4 mr-2" />
              Open in New Tab
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => handleDownloadDocument(selectedDocument.url, selectedDocument.fileName)}
              className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
            >
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </div>
        </div>
      </div>
    )}

    {/* Remove Confirmation Modal */}
    {showRemoveConfirmation && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <Trash2 className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Remove Bank Account</h3>
              <p className="text-sm text-gray-500">This action cannot be undone</p>
            </div>
          </div>
          
          <div className="mb-6">
            <p className="text-gray-700">
              Are you sure you want to remove this bank account? This will permanently delete the account and all its associated data from the database.
            </p>
            {accountToRemove !== null && localData[accountToRemove] && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-900">
                  Account: {localData[accountToRemove].accountHolderName}
                </p>
                <p className="text-sm text-gray-600">
                  Bank: {localData[accountToRemove].bankName} - {localData[accountToRemove].accountNumber}
                </p>
              </div>
            )}
          </div>
          
          <div className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={cancelRemoveAccount}
              className="px-4 py-2"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmRemoveAccount}
              className="px-4 py-2 bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Remove Account
            </Button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}; 